import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';
import {FontMetadata, FontLoadingStatus} from '@contentlab/api/fonts/types';
import {FontVariants} from '@contentlab/types/captions';

export type VariantState = {
  status: FontLoadingStatus;
  error?: string;
};

export type FontLoadingState = {
  variants: Map<FontVariants, VariantState>;
};

export type FontsStoreState = {
  fontsMetadata: Map<string, FontMetadata>; // fontId -> metadata
  loadingStates: Map<string, FontLoadingState>; // fontId -> loading state
};

const initialState: FontsStoreState = {
  fontsMetadata: new Map(),
  loadingStates: new Map(),
};

const fontsStore = create<FontsStoreState>()(
  devtools(immer(() => initialState))
);

const getAllFonts = (): FontMetadata[] => {
  const metadata = fontsStore.getState().fontsMetadata;
  return Array.from(metadata.values());
};

// Font metadata actions
const setFontMetadata = (fontId: string, metadata: FontMetadata) =>
  fontsStore.setState((state) => {
    state.fontsMetadata.set(fontId, metadata);
  });

const setFontsMetadata = (metadataArray: FontMetadata[]) =>
  fontsStore.setState((state) => {
    metadataArray.forEach((metadata) => {
      state.fontsMetadata.set(metadata.id, metadata);
    });
  });

const getFontMetadata = (fontId: string): FontMetadata | undefined => {
  return fontsStore.getState().fontsMetadata.get(fontId);
};

const getAllFontsMetadata = (): FontMetadata[] => {
  return Array.from(fontsStore.getState().fontsMetadata.values());
};

const clearFontsMetadata = () =>
  fontsStore.setState((state) => {
    state.fontsMetadata.clear();
  });

// Helper to get or create font loading state
const getOrCreateFontState = (fontId: string): FontLoadingState => {
  const currentState = fontsStore.getState().loadingStates.get(fontId);
  if (currentState) return currentState;

  return {
    variants: new Map(),
  };
};

// Individual font loading actions
const setFontLoading = (fontId: string, variant: FontVariants) =>
  fontsStore.setState((state) => {
    const fontState = getOrCreateFontState(fontId);
    fontState.variants.set(variant, {status: FontLoadingStatus.LOADING});
    state.loadingStates.set(fontId, fontState);
  });

const setFontLoaded = (fontId: string, variant: FontVariants) =>
  fontsStore.setState((state) => {
    const fontState = getOrCreateFontState(fontId);
    fontState.variants.set(variant, {status: FontLoadingStatus.LOADED});
    state.loadingStates.set(fontId, fontState);
  });

const setFontError = (fontId: string, variant: FontVariants, error: string) =>
  fontsStore.setState((state) => {
    const fontState = getOrCreateFontState(fontId);
    fontState.variants.set(variant, {status: FontLoadingStatus.ERROR, error});
    state.loadingStates.set(fontId, fontState);
  });

// Selectors for font loading state
const isFontLoaded = (fontId: string, variant: FontVariants): boolean => {
  const fontState = fontsStore.getState().loadingStates.get(fontId);
  const variantState = fontState?.variants.get(variant);
  return variantState?.status === FontLoadingStatus.LOADED;
};

const isFontLoading = (fontId: string, variant: FontVariants): boolean => {
  const fontState = fontsStore.getState().loadingStates.get(fontId);
  const variantState = fontState?.variants.get(variant);
  return variantState?.status === FontLoadingStatus.LOADING;
};

const getFontError = (fontId: string, variant: FontVariants): string | null => {
  const fontState = fontsStore.getState().loadingStates.get(fontId);
  const variantState = fontState?.variants.get(variant);
  return variantState?.status === FontLoadingStatus.ERROR
    ? (variantState.error ?? null)
    : null;
};

const getFontVariantStatus = (
  fontId: string,
  variant: FontVariants
): FontLoadingStatus => {
  const fontState = fontsStore.getState().loadingStates.get(fontId);
  const variantState = fontState?.variants.get(variant);
  return variantState?.status ?? FontLoadingStatus.NOT_LOADED;
};

export const fontsStoreActions = {
  // Font metadata actions
  setFontMetadata,
  setFontsMetadata,
  getFontMetadata,
  getAllFontsMetadata,
  clearFontsMetadata,

  // Individual font loading actions
  setFontLoading,
  setFontLoaded,
  setFontError,

  // Selectors
  getAllFonts,
  isFontLoaded,
  isFontLoading,
  getFontError,
  getFontVariantStatus,
};

export default fontsStore;
